import { Metadata } from "@dropbox/dash-component-library";
import { IconButton } from "@dropbox/dig-components/buttons";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine } from "@dropbox/dig-icons/assets";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_RemoveSource } from "@mirage/analytics/events/types/click_remove_source";
import { PAP_Click_SourceFile } from "@mirage/analytics/events/types/click_source_file";
import { Link } from "@mirage/link/Link";
import type { LayoutVariant } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/types";
import type { SourceRowParts } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import {
  composeSourceToMirage,
  formatFileSize,
  getSourceRowParts,
} from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { ChatSourceType } from "@mirage/mosaics/Chat/types";
import { getUpdatedAgoString } from "@mirage/mosaics/Chat/utils/time";
import { useAsyncAssistantFileViewer } from "@mirage/mosaics/FileViewer/AsyncFileViewer";

import { AnswerCitationCardsInOverlay } from "@mirage/mosaics/MultiAnswersCard/AnswerCitationCardsInOverlay";
import { stripStackItemFileExtension } from "@mirage/service-stacks/service/utils";
import { getSourceUrl } from "@mirage/shared/compose/compose-session";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import { FileContentIconWithThumbnail } from "@mirage/shared/icons/FileContentIcon";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n, { meticulousRedactedStringClass } from "@mirage/translations";
import classNames from "classnames";
import { memo, useMemo } from "react";
import styles from "./ConversationMessages.module.css";

export interface ConversationMessageSourceProps {
  sources: ChatSource[];
  sourceType: ChatSourceType;
  conversationId?: string;
  searchSessionId?: string;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  assistRequestId?: string;
}

/**
 * Show a list of sources as citation cards.
 */
export const ConversationMessageSource = memo(
  ({
    sources,
    sourceType,
    conversationId,
    searchSessionId,
    logComposeEvent,
    assistRequestId,
  }: ConversationMessageSourceProps) => {
    const sourcesRowParts: SourceRowParts[] = [];
    for (const source of sources) {
      const sourcesRowPart = getSourceRowParts(source, {
        icon: {
          hasBackground: false,
          size: "small",
        },
      });

      if (sourcesRowPart) {
        sourcesRowParts.push(sourcesRowPart);
      }
    }

    if (sourcesRowParts.length === 0) {
      return null;
    }

    let sourcesLabel = "";
    if (sourceType === ChatSourceType.Context) {
      sourcesLabel = i18n.t("compose_num_sources_considered", {
        count: sourcesRowParts.length,
      });
    } else {
      sourcesLabel = i18n.t("compose_num_sources", {
        count: sourcesRowParts.length,
      });
    }

    return (
      <AnswerCitationCardsInOverlay
        conversationId={conversationId}
        searchSessionId={searchSessionId}
        logComposeEvent={logComposeEvent}
        assistRequestId={assistRequestId}
        placement={"top-start"}
        sources={sources}
        refComponent={
          <Metadata
            size="small"
            withDividers="blank"
            className={styles.ConversationMessageSource}
          >
            <div className={styles.ConversationMessageSourceIcons}>
              {Array.from(
                new Map(
                  sourcesRowParts.map((item) => [
                    item.mirageResult.connectorInfo.connectorId,
                    item,
                  ]),
                ).values(),
              ).map((sourcesRowPart, i) => (
                <Metadata.Item key={i}>{sourcesRowPart.icon}</Metadata.Item>
              ))}
            </div>
            <div className={styles.ConversationMessageSourceLabel}>
              {sourcesLabel}
            </div>
          </Metadata>
        }
      />
    );
  },
);
ConversationMessageSource.displayName = "ConversationMessageSource";

export interface ConversationMessageRowMessageSourceRowProps {
  variant?: LayoutVariant;
  source: ChatSource;
  onRemoveSource?: (source: ChatSource) => void;
  padBottom?: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
}

/**
 * Show a single source as a full message row.
 */
export const ConversationMessageRowMessageSourceRow = memo(
  ({
    variant,
    source,
    onRemoveSource,
    padBottom = false,
    logComposeEvent,
    conversationId,
    searchSessionId,
    assistRequestId,
  }: ConversationMessageRowMessageSourceRowProps) => {
    const mirageResult = useMemo(() => composeSourceToMirage(source), [source]);
    const url = getSourceUrl(source);
    const { searchQueryUuid } = useSearchQueryId();
    const isEmbed = isEmbedPage();

    const sourceTitle = useMemo(() => {
      // Strips the .stackitem extension from the source title for sources in Stack Chats.
      // Stack Item sources always come back as dash_search_results within the context of a
      // conversation, so its easier to do this here, rather than in composeSourceToMirage.
      if (mirageResult) {
        return stripStackItemFileExtension(mirageResult.title);
      }

      return "";
    }, [mirageResult?.title]);

    // For full width only show 1 line of text on every source chip
    const truncationLines: number = variant === "full" ? 1 : 2;

    const handleRemoveSource: React.MouseEventHandler = (e) => {
      e.stopPropagation();
      onRemoveSource?.(source);
      logComposeEvent(PAP_Click_RemoveSource());
    };

    const fileViewer = useAsyncAssistantFileViewer({
      url,
      fileName: sourceTitle,
      handleRemoveSource,
      logComposeEvent,
    });

    // This prevents flickering of the file icons while the conversation is streaming. Ideally
    // FileContentIcon is optimized to better memoize based on its props, but for now this is
    // a quick fix for the assistant. The dependency array treats the uuid of the mirageResult
    // as the key to determining whether a new icon should be rendered or not.
    const fileIcon = useMemo(() => {
      return (
        <FileContentIconWithThumbnail
          content={mirageResult!}
          size={variant === "condensed" || isEmbed ? "medium" : "large"}
          hasBackground={false}
          shouldFetchThumbnail
        />
      );
    }, [mirageResult?.uuid, variant]);

    if (!mirageResult) {
      return null;
    }

    if (fileViewer) {
      return (
        <div className={classNames(styles.ConversationMessageSourceFileViewer)}>
          {fileViewer}
        </div>
      );
    }

    return (
      <div
        className={classNames(
          styles.ConversationMessageSourceRow,
          meticulousRedactedStringClass,
          {
            [styles.ConversationMessageSourcePadBottom]: padBottom,
            [styles.ConversationMessageSourceRowFullWidth]: variant === "full",
          },
        )}
        role="button"
        tabIndex={0}
      >
        {fileIcon}
        <div className={styles.ConversationMessageSourceInfo}>
          <Text size="small" className={styles.ConversationMessageSourceTitle}>
            {source.type === "local_file_content" ? (
              <Truncate lines={truncationLines}>{sourceTitle}</Truncate>
            ) : (
              <Link
                className={styles.ConversationMessageTitle}
                variant="monochromatic"
                href={mirageResult.url || ""}
                target="_blank"
                rel="noreferrer"
                hasNoUnderline
                onClick={() => {
                  logComposeEvent(
                    PAP_Click_SourceFile({
                      actionType: "user_input",
                      conversationId,
                      searchSessionId,
                      searchQueryUuid,
                      assistRequestId,
                      resultUuid: mirageResult.uuid,
                    }),
                  );
                }}
              >
                <Truncate lines={truncationLines}>{sourceTitle}</Truncate>
              </Link>
            )}
          </Text>
          <Text
            size="small"
            className={styles.ConversationMessageSourceMetadata}
            color="subtle"
          >
            <Metadata.Item>
              <Metadata.Label>
                {source.type === "local_file_content"
                  ? formatFileSize(source.fileSize || 0)
                  : getUpdatedAgoString(mirageResult)}
              </Metadata.Label>
            </Metadata.Item>
          </Text>
        </div>
        {onRemoveSource && (
          <DigTooltip
            title={i18n.t("compose_source_row_remove_button_label")}
            placement="top"
          >
            <IconButton
              variant="transparent"
              className={styles.ComposeSourceActionButton}
              size="small"
              shape="standard"
              onClick={handleRemoveSource}
            >
              <UIIcon src={CloseLine} />
            </IconButton>
          </DigTooltip>
        )}
      </div>
    );
  },
);
ConversationMessageRowMessageSourceRow.displayName =
  "ConversationMessageRowMessageSourceRow";
